import React, { useState } from 'react';
import { Info, HelpCircle } from 'lucide-react';

const CorrelationMatrix = ({ data }) => {
    const [hoveredCell, setHoveredCell] = useState(null);

    if (!data || !data.matrix || data.matrix.length < 2) {
        return (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400 bg-white rounded-2xl border border-dashed border-slate-200">
                <Info size={32} className="mb-2 opacity-50" />
                <p className="font-medium">Pas assez de données pour la corrélation.</p>
                <p className="text-xs mt-1">Il faut au moins 2 positions avec un historique commun.</p>
            </div>
        );
    }

    const { symbols, matrix } = data;

    // Fonction couleur : Vert (Décorrélé) -> Rouge (Corrélé)
    const getColor = (val) => {
        if (val === 1) return 'bg-slate-100'; // Diagonale
        
        if (val > 0.8) return 'bg-rose-500 text-white';
        if (val > 0.5) return 'bg-rose-300 text-slate-900';
        if (val > 0.2) return 'bg-orange-200 text-slate-900';
        if (val > -0.2) return 'bg-emerald-100 text-slate-900';
        return 'bg-emerald-500 text-white';
    };

    return (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h3 className="font-bold text-slate-800 text-lg">Matrice de Corrélation</h3>
                    <p className="text-xs text-slate-500">Mesure la dépendance entre vos actifs sur 1 an.</p>
                </div>
                <div className="text-right">
                    <div className="text-xs font-bold text-slate-400 uppercase mb-1">Légende</div>
                    <div className="flex gap-2 text-[10px] font-bold">
                        <span className="px-2 py-1 bg-emerald-500 text-white rounded">Diversifiant</span>
                        <span className="px-2 py-1 bg-rose-500 text-white rounded">Redondant</span>
                    </div>
                </div>
            </div>
            
            <div className="overflow-x-auto pb-4">
                <div className="inline-block min-w-full">
                    <div className="grid" style={{ gridTemplateColumns: `auto repeat(${symbols.length}, minmax(40px, 1fr))` }}>
                        
                        {/* Header Row */}
                        <div className="h-24 w-24"></div> {/* Coin vide */}
                        {symbols.map((s, i) => (
                            <div key={s} className="relative h-24 flex items-end justify-center pb-2">
                                <span className="absolute transform -rotate-45 origin-bottom-left translate-x-4 -translate-y-2 text-xs font-bold text-slate-600 whitespace-nowrap">
                                    {s}
                                </span>
                            </div>
                        ))}

                        {/* Matrix Rows */}
                        {matrix.map((row, i) => (
                            <React.Fragment key={i}>
                                {/* Row Label */}
                                <div className="h-10 flex items-center justify-end pr-4 text-xs font-bold text-slate-600">
                                    {symbols[i]}
                                </div>
                                
                                {/* Cells */}
                                {row.map((val, j) => (
                                    <div 
                                        key={`${i}-${j}`}
                                        onMouseEnter={() => setHoveredCell({ val, s1: symbols[i], s2: symbols[j] })}
                                        onMouseLeave={() => setHoveredCell(null)}
                                        className={`h-10 border border-white flex items-center justify-center text-[10px] font-bold cursor-default transition-all hover:scale-105 hover:shadow-lg hover:z-10 rounded-sm ${getColor(val)}`}
                                    >
                                        {val === 1 ? '' : val.toFixed(2)}
                                    </div>
                                ))}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>

            {/* Tooltip flottant contextuel */}
            <div className="mt-2 h-6 text-center">
                {hoveredCell ? (
                    <span className="text-sm font-medium text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                        Corrélation entre <span className="font-bold text-slate-900">{hoveredCell.s1}</span> et <span className="font-bold text-slate-900">{hoveredCell.s2}</span> : <span className={`font-bold ${hoveredCell.val > 0.5 ? 'text-rose-600' : 'text-emerald-600'}`}>{hoveredCell.val.toFixed(2)}</span>
                    </span>
                ) : (
                    <span className="text-xs text-slate-400 italic flex items-center justify-center gap-1">
                        <HelpCircle size={12}/> Survolez une case pour voir les détails
                    </span>
                )}
            </div>
        </div>
    );
};

export default CorrelationMatrix;
